/**
 * providerData.js
 *
 * Datos estáticos de todos los operadores de coliving indexados en Coolliving.
 * Se usa en ColivingAdvancedSearch.vue para generar el contenido SEO de las páginas
 * de proveedor (/encuentratucoliving/:provider y /findyourcoliving/:provider).
 *
 * Estructura de cada entrada:
 *   fullName    {string}   Nombre comercial visible
 *   description {string}   Descripción breve para el bloque SEO
 *   cities      {string[]} Ciudades / áreas donde opera
 *   priceRange  {string}   Rango de precios (ej. "500€ - 900€")
 *   avgPrice    {number}   Precio medio mensual (para ordenación/filtros)
 *   highlights  {string[]} 4-5 bullet points de propuesta de valor
 *   keywords    {string}   Keywords SEO separadas por coma
 *   forStudents {boolean?} true si es principalmente residencia de estudiantes
 */

export const providerInfo = {

  // ──────────────────────────────────────────────────────────────
  // ESPAÑA — MADRID
  // ──────────────────────────────────────────────────────────────
  'urbancampus': {
    fullName: 'Urban Campus',
    description: 'Urban Campus es uno de los operadores de coliving más grandes de España, con edificios modernos en Madrid y Barcelona. Ofrecen espacios diseñados para profesionales y estudiantes con zonas comunes, coworking y eventos de networking.',
    cities: ['Madrid', 'Barcelona'],
    priceRange: '600€ - 1200€',
    avgPrice: 850,
    highlights: ['Zonas de coworking incluidas', 'Eventos de networking semanales', 'Edificios completamente nuevos', 'Comunidad internacional'],
    keywords: 'urban campus, urban campus madrid, urban campus barcelona, coliving urban campus'
  },
  'gaiastays': {
    fullName: 'GaiaStays',
    description: 'GaiaStays es un operador líder en alojamiento flexible. Sus colivings destacan por ubicaciones céntricas, flexibilidad de contratos y comunidad vibrante de profesionales y nómadas digitales.',
    cities: ['Madrid', 'Barcelona', 'Valencia'],
    priceRange: '500€ - 900€',
    avgPrice: 700,
    highlights: ['Contratos flexibles desde 1 mes', 'Ubicaciones céntricas', 'App para gestión fácil', 'Comunidad activa'],
    keywords: 'gaiastays, gaia stays, gaiastays madrid, gaiastays barcelona, olala homes'
  },
  'kanso': {
    fullName: 'Kanso Coliving',
    description: 'Kanso Coliving ofrece espacios minimalistas y funcionales en Madrid. Con filosofía de diseño japonés, sus colivings son perfectos para quienes buscan tranquilidad, orden y comunidad consciente.',
    cities: ['Madrid'],
    priceRange: '550€ - 800€',
    avgPrice: 650,
    highlights: ['Diseño minimalista japonés', 'Todo incluido (WiFi, limpieza, gastos)', 'Comunidad consciente', 'Ubicaciones residenciales tranquilas'],
    keywords: 'kanso coliving, kanso madrid, coliving kanso'
  },
  'muppy': {
    fullName: 'Muppy',
    description: 'Muppy es un operador de coliving enfocado en jóvenes profesionales. Sus espacios son modernos, bien equipados y ubicados estratégicamente cerca de zonas business y universidades.',
    cities: ['Madrid'],
    priceRange: '500€ - 750€',
    avgPrice: 600,
    highlights: ['Perfecto para jóvenes profesionales', 'Cerca de zonas business', 'Espacios modernos', 'Gestión 100% digital'],
    keywords: 'muppy, muppy coliving, muppy madrid'
  },
  'enso': {
    fullName: 'Enso Coliving',
    description: 'Enso Coliving combina diseño, comunidad y sostenibilidad. Sus espacios están pensados para fomentar la creatividad y el networking entre residentes de diversos backgrounds.',
    cities: ['Madrid', 'Barcelona'],
    priceRange: '550€ - 850€',
    avgPrice: 680,
    highlights: ['Enfoque en sostenibilidad', 'Comunidad creativa', 'Eventos culturales', 'Diseño contemporáneo'],
    keywords: 'enso coliving, enso madrid, enso barcelona'
  },
  'homiii': {
    fullName: 'Homiii',
    description: 'Homiii ofrece coliving asequible con excelente relación calidad-precio. Sus espacios son acogedores, bien mantenidos y perfectos para estancias de media a larga duración.',
    cities: ['Madrid', 'Barcelona'],
    priceRange: '450€ - 700€',
    avgPrice: 550,
    highlights: ['Excelente relación calidad-precio', 'Espacios acogedores', 'Gestión profesional', 'Contratos flexibles'],
    keywords: 'homiii, homiii coliving, homiii madrid, homiii barcelona'
  },
  'spacest': {
    fullName: 'Spacest',
    description: 'Spacest es un operador de coliving premium con edificios de última generación. Ofrecen amenities de lujo como gym, rooftop, salas de cine y espacios de coworking profesionales.',
    cities: ['Madrid'],
    priceRange: '700€ - 1100€',
    avgPrice: 900,
    highlights: ['Amenities premium (gym, rooftop)', 'Edificios de lujo', 'Coworking profesional', 'Servicio de conserjería'],
    keywords: 'spacest, spacest coliving, spacest madrid'
  },
  'aticco': {
    fullName: 'Aticco Living',
    description: 'Aticco Living es la rama residencial de Aticco, famoso por sus espacios de coworking. Combinan alojamiento con coworking de primera categoría, ideal para emprendedores y freelancers.',
    cities: ['Barcelona'],
    priceRange: '650€ - 950€',
    avgPrice: 800,
    highlights: ['Coworking incluido', 'Comunidad emprendedora', 'Eventos de networking', 'Ubicación céntrica Barcelona'],
    keywords: 'aticco living, aticco barcelona, aticco coliving'
  },
  'haaus': {
    fullName: 'Haaus',
    description: 'Haaus ofrece coliving con enfoque en diseño y comunidad. Sus espacios destacan por ser Instagram-worthy, con zonas comunes amplias y decoración cuidada.',
    cities: ['Madrid', 'Barcelona'],
    priceRange: '550€ - 850€',
    avgPrice: 700,
    highlights: ['Diseño Instagram-worthy', 'Zonas comunes amplias', 'Comunidad joven', 'Eventos sociales frecuentes'],
    keywords: 'haaus, haaus coliving, haaus madrid'
  },
  'socien': {
    fullName: 'Socien',
    description: 'Socien es un operador de coliving con enfoque social. Organizan eventos semanales, talleres y actividades para fomentar la conexión entre residentes.',
    cities: ['Madrid'],
    priceRange: '500€ - 750€',
    avgPrice: 620,
    highlights: ['Eventos semanales incluidos', 'Talleres y workshops', 'Comunidad muy activa', 'Gestión cercana'],
    keywords: 'socien, socien coliving, socien madrid'
  },
  'suiters': {
    fullName: 'Suiters',
    description: 'Suiters ofrece apartamentos compartidos premium en Madrid. Sus espacios están completamente amueblados y equipados, con servicios de limpieza y mantenimiento incluidos.',
    cities: ['Madrid'],
    priceRange: '600€ - 900€',
    avgPrice: 750,
    highlights: ['Apartamentos premium', 'Limpieza incluida', 'Mantenimiento 24/7', 'Ubicaciones prime'],
    keywords: 'suiters, suiters madrid, suiters coliving'
  },
  'houxury': {
    fullName: 'Houxury',
    description: 'Houxury es coliving de lujo para profesionales exigentes. Ofrecen servicios premium como housekeeping diario, conserjería y amenities exclusivos.',
    cities: ['Madrid', 'Barcelona'],
    priceRange: '800€ - 1500€',
    avgPrice: 1100,
    highlights: ['Coliving de lujo', 'Housekeeping diario', 'Conserjería 24/7', 'Amenities exclusivos'],
    keywords: 'houxury, houxury coliving, houxury madrid'
  },
  'colivinn': {
    fullName: 'ColivINN',
    description: 'ColivINN ofrece espacios de coliving modernos y accesibles. Sus colivings están diseñados para estudiantes y jóvenes profesionales que buscan ambiente internacional.',
    cities: ['Madrid', 'Barcelona'],
    priceRange: '500€ - 750€',
    avgPrice: 600,
    highlights: ['Ambiente internacional', 'Perfecto para estudiantes', 'Zonas de estudio', 'Eventos culturales'],
    keywords: 'colivinn, coliv inn, colivinn madrid'
  },
  'trc': {
    fullName: 'The Residential Club',
    description: 'The Residential Club ofrece coliving exclusivo para profesionales en Madrid y Málaga. Edificios con gym, piscina, coworking y servicios tipo hotel. En Málaga: San Rafael (centro) y Blink Tech Park (PTA).',
    cities: ['Madrid', 'Málaga'],
    priceRange: '450€ - 1600€',
    avgPrice: 900,
    highlights: ['Servicios premium', 'Madrid y Málaga', 'Flex living PTA', 'Comunidad profesional'],
    keywords: 'the residential club, trc madrid, trc malaga, residential club'
  },
  'node': {
    fullName: 'Node Living',
    description: 'Node Living es un operador de coliving tech-friendly. Sus espacios están equipados con última tecnología, WiFi de alta velocidad y zonas de coworking diseñadas para trabajadores remotos.',
    cities: ['Madrid', 'Barcelona'],
    priceRange: '600€ - 950€',
    avgPrice: 750,
    highlights: ['Tech-friendly', 'WiFi ultra-rápido', 'Coworking diseñado para remote workers', 'Smart rooms'],
    keywords: 'node living, node coliving, node madrid'
  },
  'colive': {
    fullName: 'Colive',
    description: 'Colive.es es una plataforma española que conecta a inquilinos con habitaciones en pisos compartidos. Ofrecen opciones asequibles en las principales ciudades de España.',
    cities: ['Madrid', 'Barcelona', 'Valencia', 'Sevilla'],
    priceRange: '350€ - 600€',
    avgPrice: 450,
    highlights: ['Precios muy competitivos', 'Gran variedad de opciones', 'Proceso de reserva simple', 'Cobertura nacional'],
    keywords: 'colive, colive españa, colive madrid, colive barcelona'
  },
  'trees': {
    fullName: 'Trees Coliving',
    description: 'Trees Coliving tiene enfoque ecológico y sostenible. Sus espacios están diseñados con materiales naturales y promueven un estilo de vida conscious living.',
    cities: ['Madrid', 'Barcelona'],
    priceRange: '550€ - 800€',
    avgPrice: 670,
    highlights: ['Enfoque ecológico', 'Materiales sostenibles', 'Conscious living', 'Jardines y terrazas verdes'],
    keywords: 'trees coliving, trees madrid, coliving sostenible'
  },
  'resa': {
    fullName: 'RESA',
    description: 'RESA es el mayor operador de residencias de estudiantes en España. Con más de 60 centros, ofrecen alojamiento premium para universitarios con servicios all-inclusive.',
    cities: ['Madrid', 'Barcelona', 'Valencia', 'Sevilla', 'Granada', 'Salamanca'],
    priceRange: '600€ - 1100€',
    avgPrice: 850,
    highlights: ['Líder en residencias universitarias', 'Servicios all-inclusive', 'Cerca de universidades', 'Eventos y actividades diarias'],
    keywords: 'resa, resa residencia estudiantes, resa madrid, resa barcelona',
    forStudents: true
  },
  'yugo': {
    fullName: 'Yugo',
    description: 'Yugo ofrece residencias de estudiantes de última generación con amenities modernos. Sus espacios están diseñados para la vida universitaria del siglo XXI.',
    cities: ['Madrid', 'Barcelona'],
    priceRange: '700€ - 1200€',
    avgPrice: 900,
    highlights: ['Residencias premium', 'Gym y zonas comunes modernas', 'Eventos para estudiantes', 'App de gestión'],
    keywords: 'yugo, yugo residencia, yugo madrid, yugo estudiantes',
    forStudents: true
  },
  'livensa': {
    fullName: 'Livensa Living',
    description: 'Livensa Living es un operador premium de residencias de estudiantes. Sus edificios son de diseño contemporáneo con servicios de hotel adaptados a estudiantes.',
    cities: ['Madrid', 'Barcelona', 'Valencia'],
    priceRange: '650€ - 1000€',
    avgPrice: 820,
    highlights: ['Diseño contemporáneo', 'Servicios de hotel', 'Zonas de estudio 24/7', 'Comunidad internacional'],
    keywords: 'livensa living, livensa, livensa madrid, livensa estudiantes',
    forStudents: true
  },
  'collegiate': {
    fullName: 'Collegiate',
    description: 'Collegiate es un operador internacional de residencias de estudiantes con presencia en España. Ofrecen experiencia universitaria completa con eventos, talleres y networking.',
    cities: ['Madrid', 'Barcelona'],
    priceRange: '750€ - 1300€',
    avgPrice: 950,
    highlights: ['Red internacional', 'Eventos y talleres semanales', 'Networking con estudiantes de todo el mundo', 'Instalaciones de lujo'],
    keywords: 'collegiate, collegiate madrid, collegiate barcelona, collegiate estudiantes',
    forStudents: true
  },

  // ──────────────────────────────────────────────────────────────
  // ESPAÑA — OPERADORES NUEVOS / RESTO CIUDADES
  // ──────────────────────────────────────────────────────────────
  'flipco': {
    fullName: 'Flipco Living',
    description: 'Flipco Living ofrece coliving y flex living en Madrid y Málaga. En Madrid: Tetuán y Retiro. En Málaga: Costa del Sol en Plaza de la Merced con coworking y zonas comunes.',
    cities: ['Madrid', 'Málaga'],
    priceRange: '650€ - 1400€',
    avgPrice: 720,
    highlights: ['Madrid y Málaga', 'Todo incluido', 'Coworking', 'Estancias flexibles'],
    keywords: 'flipco, flipco living, flipco madrid, flipco malaga, coliving flipco'
  },
  'liveit': {
    fullName: 'Live it (SmartRental)',
    description: 'Live it, parte de SmartRental, ofrece apartamentos de coliving de alta gama en Madrid, Barcelona y Málaga. Sus espacios combinan lujo, confort y servicios premium.',
    cities: ['Madrid', 'Barcelona', 'Málaga'],
    priceRange: '750€ - 1100€',
    avgPrice: 900,
    highlights: ['Parte de SmartRental', 'Alta gama', 'Servicios premium', 'Ubicaciones prime'],
    keywords: 'live it, liveit, smartrental coliving, live it madrid'
  },
  'habyt': {
    fullName: 'Habyt',
    description: 'Habyt es uno de los mayores operadores de coliving a nivel global con presencia en Madrid. Ofrecen habitaciones amuebladas todo incluido con contratos flexibles y proceso 100% digital.',
    cities: ['Madrid'],
    priceRange: '700€ - 1000€',
    avgPrice: 850,
    highlights: ['Operador global', 'Proceso 100% digital', 'Contratos super flexibles', 'Sin depósito tradicional'],
    keywords: 'habyt, habyt madrid, habyt coliving, habyt españa'
  },
  'queens': {
    fullName: 'Queens Coliving',
    description: 'Queens Coliving ofrece espacios de vida compartida premium en Madrid y Barcelona. Sus colivings destacan por habitaciones amplias con baño en suite y zonas comunes de calidad.',
    cities: ['Madrid', 'Barcelona'],
    priceRange: '700€ - 900€',
    avgPrice: 800,
    highlights: ['Habitaciones con baño en suite', 'Zonas comunes premium', 'Ubicaciones céntricas', 'Comunidad internacional'],
    keywords: 'queens coliving, queens madrid, queens barcelona, coliving queens'
  },
  'socialhub': {
    fullName: 'The Social Hub',
    description: 'The Social Hub (antes The Student Hotel) es un concepto híbrido de coliving-hotel con presencia en Madrid, Barcelona y San Sebastián. Ofrecen servicios de hotel con comunidad de coliving.',
    cities: ['Madrid', 'Barcelona', 'San Sebastián'],
    priceRange: '850€ - 1200€',
    avgPrice: 980,
    highlights: ['Concepto híbrido hotel-coliving', 'Gym y restaurante incluidos', 'Coworking profesional', 'Rooftop y eventos'],
    keywords: 'the social hub, social hub madrid, social hub barcelona, student hotel'
  },
  'outsite': {
    fullName: 'Outsite',
    description: 'Outsite es un operador de coliving internacional especializado en nómadas digitales. Espacios Signature en Barcelona e Ibiza con coworking, comunidad global y estancias flexibles.',
    cities: ['Barcelona', 'Ibiza'],
    priceRange: '1200€ - 1400€',
    avgPrice: 1275,
    highlights: ['Para nómadas digitales', 'Red internacional de colivings', 'Coworking 24/7', 'Comunidad global'],
    keywords: 'outsite, outsite barcelona, outsite coliving, coliving nomadas digitales'
  },
  'mycoliving': {
    fullName: 'MyCO Living',
    description: 'MyCO Living es el primer coliving de Valencia, con múltiples ubicaciones en el centro histórico. Ofrecen habitaciones con personalidad en edificios reformados con encanto.',
    cities: ['Valencia'],
    priceRange: '550€ - 850€',
    avgPrice: 700,
    highlights: ['El primer coliving de Valencia', 'Centro histórico', 'Edificios con encanto', 'Comunidad local'],
    keywords: 'myco living, mycoliving valencia, myco valencia, primer coliving valencia'
  },
  'becasa': {
    fullName: 'Be Casa Essential',
    description: 'Be Casa Essential es un gran desarrollo de coliving en Málaga con más de 130 pisos y 46 habitaciones de coliving. Un concepto moderno de vida compartida en la Costa del Sol.',
    cities: ['Málaga'],
    priceRange: '550€ - 800€',
    avgPrice: 650,
    highlights: ['Proyecto grande (177 unidades)', 'Gimnasio incluido', 'Coworking', 'Costa del Sol'],
    keywords: 'be casa essential, becasa malaga, coliving malaga, be casa'
  },
  'oceanika': {
    fullName: 'Oceanika (Habyt)',
    description: 'Oceanika, operado por Habyt, es el mayor coliving de Málaga con 180 apartamentos en Torremolinos. Ofrece piscina, gimnasio y está a minutos de la playa.',
    cities: ['Torremolinos', 'Málaga'],
    priceRange: '650€ - 900€',
    avgPrice: 750,
    highlights: ['El mayor coliving de Málaga', '180 apartamentos', 'Piscina y gimnasio', 'Cerca de la playa'],
    keywords: 'oceanika, oceanika habyt, oceanika torremolinos, coliving torremolinos'
  },
  'nova': {
    fullName: 'Nova Málaga',
    description: 'Nova Málaga es una residencia de coliving para estudiantes en el centro de Málaga. Ofrece habitaciones asequibles con zonas de estudio y ambiente universitario.',
    cities: ['Málaga'],
    priceRange: '450€ - 650€',
    avgPrice: 550,
    highlights: ['Para estudiantes', 'Precios asequibles', 'Centro de Málaga', 'Zonas de estudio'],
    keywords: 'nova malaga, nova coliving estudiantes, residencia estudiantes malaga',
    forStudents: true
  },
  'thr': {
    fullName: 'THR Coliving',
    description: 'THR Coliving gestiona pisos compartidos en Málaga sin comisiones de agencia. 8 ubicaciones en Perchel, Teatinos, Soho, Pedregalejo y más. Habitaciones amuebladas con suministros incluidos desde 440€/mes.',
    cities: ['Málaga'],
    priceRange: '440€ - 550€',
    avgPrice: 480,
    highlights: ['Sin comisiones', '8 pisos en Málaga', 'Suministros incluidos', 'Estancia flexible'],
    keywords: 'thr coliving, thr coliving malaga, coliving malaga barato'
  },
  'alegoria': {
    fullName: 'Alegoría Coliving Experience',
    description: 'Alegoría Coliving Experience ofrece una experiencia de coliving única en el centro histórico de Sevilla. Ubicado en un edificio con encanto, combina tradición y modernidad.',
    cities: ['Sevilla'],
    priceRange: '500€ - 750€',
    avgPrice: 600,
    highlights: ['Centro histórico de Sevilla', 'Edificio con encanto', 'Experiencia única', 'Comunidad de profesionales'],
    keywords: 'alegoria coliving, alegoria sevilla, coliving sevilla centro'
  },
  'olivar': {
    fullName: 'Olivar Plaza Cohousing',
    description: 'Olivar Plaza es un innovador proyecto de cohousing senior en Gines (Sevilla). Con 35 apartamentos, ofrece comunidad, servicios compartidos y calidad de vida para mayores activos.',
    cities: ['Gines', 'Sevilla'],
    priceRange: '650€ - 900€',
    avgPrice: 750,
    highlights: ['Cohousing senior', '35 apartamentos', 'Comunidad activa', 'Servicios compartidos'],
    keywords: 'olivar plaza, cohousing sevilla, cohousing senior, olivar gines'
  },
  'thestart': {
    fullName: 'The Start',
    description: 'The Start es un coliving premium en Madrid con dos ubicaciones exclusivas: El Plantío (Majadahonda) y Aravaca. Ofrecen chalets de lujo con piscinas interiores y exteriores, gimnasio, coworking, bar privado y zonas comunes de alta gama. Ideal para profesionales que buscan calidad de vida en zonas residenciales exclusivas.',
    cities: ['Madrid'],
    priceRange: '850€ - 1350€',
    avgPrice: 1050,
    highlights: ['Piscina interior y exterior', 'Gimnasio y crossfit', 'Coworking y zonas comunes premium', 'Zonas residenciales exclusivas', 'Pet friendly (Aravaca)'],
    keywords: 'the start, thestart coliving, coliving premium madrid, coliving aravaca, coliving majadahonda'
  },
  'flaternity': {
    fullName: 'Flaternity',
    description: 'Flaternity ofrece habitaciones privadas en pisos compartidos en Barcelona y Valencia. Todos los gastos incluidos (WiFi, agua, electricidad, limpieza). Contratos flexibles desde 1 mes, ideal para profesionales, estudiantes y nómadas digitales.',
    cities: ['Barcelona', 'Valencia'],
    priceRange: '460€ - 775€',
    avgPrice: 600,
    highlights: ['Gastos incluidos', 'Contratos flexibles desde 1 mes', 'Habitaciones privadas amuebladas', 'Ubicaciones céntricas en Barcelona y Valencia'],
    keywords: 'flaternity, flaternity barcelona, flaternity valencia, coliving barcelona, habitaciones barcelona, pisos compartidos barcelona'
  },
  'hive': {
    fullName: 'Grupo Hive',
    description: 'Grupo Hive es líder en Flex Living y alquiler residencial en España. Ofrece habitaciones, estudios y pisos completos en Madrid y Málaga, tanto para media estancia (1-11 meses) como larga estancia. Gestión profesional de propiedades con WiFi y suministros incluidos.',
    cities: ['Madrid', 'Málaga'],
    priceRange: '890€ - 3.050€',
    avgPrice: 1800,
    highlights: ['Flex Living media y larga estancia', 'Gestión profesional', 'Habitaciones y pisos completos', 'Madrid y Málaga'],
    keywords: 'grupo hive, hive coliving, hive madrid, hive malaga, flex living madrid, alquiler temporal madrid, habitaciones madrid, pisos alquiler malaga'
  },
  'nomad': {
    fullName: 'Nomad Flex Living',
    description: 'Nomad Flex Living ofrece alquiler temporal flexible en Madrid, ideal para estancias de 1 a 11 meses. Sus viviendas están completamente amuebladas y equipadas, con suministros incluidos y asistencia 24/7. Muchas propiedades cuentan con piscina, garaje y terraza. Perfecto para profesionales en transición, expatriados o quienes buscan flexibilidad sin compromisos a largo plazo.',
    cities: ['Madrid'],
    priceRange: '1400€ - 2150€',
    avgPrice: 1700,
    highlights: ['Alquiler flexible (1-11 meses)', 'Viviendas totalmente equipadas', 'Suministros incluidos', 'Asistencia 24/7', 'Mascotas permitidas (mayoría)', 'Piscina y garaje (muchas propiedades)'],
    keywords: 'nomad flex living, nomad madrid, alquiler temporal madrid, coliving flexible madrid, apartamentos temporales madrid'
  },
  'farhome': {
    fullName: 'Far Home Coliving',
    description: 'Far Home ofrece coliving económico en ubicaciones premium de Madrid: Plaza Mayor (centro histórico) y Bernabéu. Con diseño retro-moderno, destacan por su terraza sostenible con paneles solares en Bernabéu, espacio coworking y bicicletas gratis. Habitaciones desde 700€/mes y estudios desde 1.200€/mes, todo incluido: gastos, wifi, limpieza semanal. Recepción 24h y actividades organizadas. Ideal para quienes buscan comunidad en el corazón de Madrid con excelente relación calidad-precio.',
    cities: ['Madrid'],
    priceRange: '700€ - 1400€',
    avgPrice: 950,
    highlights: ['2 ubicaciones premium (Centro y Bernabéu)', 'Desde 700€/mes todo incluido', 'Terraza sostenible con paneles solares', 'Espacio coworking', 'Bicicletas gratis', 'Limpieza semanal', 'Recepción 24h', 'Actividades organizadas'],
    keywords: 'far home, far home madrid, coliving plaza mayor, coliving bernabeu, coliving economico madrid, coliving centro madrid'
  },
  'repeople': {
    fullName: 'Repeople Coliving',
    description: 'Repeople es una comunidad pionera de coliving y coworking en las Islas Canarias (Gran Canaria y Tenerife). Con 6 ubicaciones estratégicas en Las Palmas y Santa Cruz, ofrecen espacios inspiradores para profesionales, emprendedores y trabajadores remotos. Destacan por su internet de fibra óptica 600/600 Mbps, acceso gratuito a coworking 24/7, limpieza semanal, café ilimitado y eventos organizados. Desde villas con piscina en Ciudad Jardín hasta colivings con azotea frente al mar. Desde 600€/mes todo incluido. El destino perfecto para trabajar con sol, playa y comunidad internacional.',
    cities: ['Las Palmas', 'Tenerife'],
    priceRange: '600€ - 900€',
    avgPrice: 700,
    highlights: ['6 ubicaciones en Islas Canarias', 'Internet 600/600 Mbps fibra óptica', 'Coworking gratuito 24/7', 'Limpieza semanal incluida', 'Café ilimitado', 'Eventos y actividades', 'Frente al mar y en ciudad', 'Comunidad internacional', 'Desde 600€/mes'],
    keywords: 'repeople, repeople canarias, coliving gran canaria, coliving tenerife, coliving las palmas, coliving santa cruz, coworking canarias, nomadas digitales canarias, trabajar en canarias'
  },
  'maraya': {
    fullName: 'Maraya Coliving',
    description: 'Maraya Coliving es un oasis de bienestar en Candelaria, Tenerife, diseñado para profesionales remotos que buscan equilibrio entre trabajo y vida. Con 9 habitaciones únicas (desde dorms compartidos hasta ensuite dobles con vistas al océano), Maraya incluye yoga diario, desayuno vegetariano, cena 4x/semana, piscina gigante y múltiples espacios de coworking. Cada habitación tiene diseño temático inspirador (japonés zen, africano, art déco 80s). Comunidad internacional vibrante con eventos regulares. WiFi de alta velocidad. Desde 48€/noche (dorm) hasta 122€/noche (ensuite doble). Descuentos semanales (20%) y mensuales (30%). Estancia mínima 2 semanas (3 noches en verano). El lugar perfecto para vivir, trabajar y conectar en el paraíso canario.',
    cities: ['Tenerife'],
    priceRange: '940€ - 2391€',
    avgPrice: 1500,
    highlights: ['9 habitaciones con diseño único', 'Yoga diario incluido', 'Desayuno vegetariano incluido', 'Cena 4x/semana incluida', 'Piscina gigante', 'Múltiples espacios coworking', 'Vistas al océano/jardín', 'Comunidad internacional', 'Descuento 30% mensual', 'WiFi alta velocidad', 'Actividades y eventos'],
    keywords: 'maraya coliving, maraya tenerife, coliving candelaria, coliving yoga tenerife, coliving piscina tenerife, coliving wellness tenerife, coliving santa cruz, trabajar en tenerife, coliving con comidas incluidas'
  },
  'vivarium': {
    fullName: 'Vivarium Coliving & Coworking',
    description: 'Vivarium es un coliving community-first en el Eixample de Valencia, junto a Ruzafa. 24 habitaciones privadas con baño en suite, coworking dedicado de 100 m² y fibra hasta 1 Gbps. Comunidad de nómadas, emprendedores y creativos con eventos semanales.',
    cities: ['Valencia'],
    priceRange: '985€ - 1200€',
    avgPrice: 1095,
    highlights: ['24 habitaciones en suite', 'Coworking 100 m²', 'Fibra 1 Gbps', 'Comunidad activa', 'Eixample/Ruzafa'],
    keywords: 'vivarium coliving, vivarium valencia, coliving eixample valencia, coliving ruzafa'
  },
  'fourwanders': {
    fullName: '4wanders',
    description: '4wanders ofrece coliving en los mejores barrios de Valencia: El Cabanyal, Camins al Grau y Eixample. Habitaciones amuebladas, coworking, eventos de comunidad y estancias flexibles desde 20 noches.',
    cities: ['Valencia'],
    priceRange: '750€ - 1920€',
    avgPrice: 1100,
    highlights: ['Varios edificios en Valencia', 'Coworking incluido', 'Eventos de comunidad', 'Cabanyal y Eixample'],
    keywords: '4wanders valencia, coliving cabanyal, coliving valencia 4wanders'
  },
  'cohausing': {
    fullName: 'Cohausing Cabanyal',
    description: 'Coliving en El Cabanyal con 12 habitaciones en suite, coworking en planta baja y a 500 m de la playa. Ideal para estancias temporales de estudiantes y profesionales.',
    cities: ['Valencia'],
    priceRange: '930€ - 1100€',
    avgPrice: 1110,
    highlights: ['El Cabanyal', 'Coworking', 'Baño privado', 'Cerca de la playa'],
    keywords: 'cohausing valencia, coliving cabanyal valencia'
  },
  'cotown': {
    fullName: 'Cotown',
    description: 'Cotown revoluciona el coliving con pisos compartidos en Valencia y Bilbao. Todo incluido: WiFi, suministros, limpieza y trámites administrativos. Estancias flexibles de 1 a 11 meses.',
    cities: ['Valencia', 'Bilbao'],
    priceRange: '419€ - 1088€',
    avgPrice: 680,
    highlights: ['Valencia y Bilbao', 'Todo incluido', 'Flexible', 'Jóvenes profesionales'],
    keywords: 'cotown valencia, cotown bilbao, coliving cotown, piso compartido bilbao'
  },
  'moontels': {
    fullName: 'Moontels',
    description: 'Moontels es una red de colivings y flex living en Valencia con 10 edificios en Ciutat Vella, Ruzafa, Ribera y otros barrios. Habitaciones amuebladas para estudiantes y jóvenes profesionales con recepción 24/7, WiFi y acceso digital.',
    cities: ['Valencia'],
    priceRange: '480€ - 650€',
    avgPrice: 540,
    highlights: ['10 edificios en Valencia', 'Estudiantes y profesionales', 'Recepción 24/7', 'Ruzafa y centro'],
    keywords: 'moontels valencia, coliving moontels, flex living valencia'
  },
  'bluerooms': {
    fullName: 'Bluerooms',
    description: 'Bluerooms gestiona 9 pisos coliving en Valencia con habitaciones privadas desde 440€/mes. Zonas como Ciutat Vella, Poblats Marítims y Camins al Grau. Todo incluido con limpieza y WiFi.',
    cities: ['Valencia'],
    priceRange: '440€ - 630€',
    avgPrice: 490,
    highlights: ['9 pisos coliving', 'Precios desde 440€', 'Ciutat Vella y playa', 'Todo incluido'],
    keywords: 'bluerooms valencia, coliving bluerooms, habitaciones valencia'
  },
  'nido': {
    fullName: 'Nido Living',
    description: 'Nido Living opera residencias premium de estudiantes en Madrid (Príncipe Pío, Plaza Castilla, Aravaca), Valencia (Malvarrosa) y Sevilla (El Porvenir). Gimnasio, piscina, coworking y todos los gastos incluidos.',
    cities: ['Madrid', 'Valencia', 'Sevilla'],
    priceRange: '450€ - 1313€',
    avgPrice: 850,
    highlights: ['Residencia premium', 'Madrid, Valencia y Sevilla', 'Gimnasio y terrazas', 'Estudiantes'],
    keywords: 'nido living, nido madrid, nido principe pio, nido plaza castilla, nido aravaca, nido malvarrosa, nido sevilla'
  },
  'palmacoliving': {
    fullName: 'Palma Coliving',
    description: 'Palma Coliving ofrece una villa coliving-coworking de 750 m² en Patacona, Valencia. 10 habitaciones privadas, jardín, coworking con fibra y comunidad de nómadas digitales. Estancia mínima 30 noches.',
    cities: ['Valencia'],
    priceRange: '864€ - 1200€',
    avgPrice: 950,
    highlights: ['Villa junto a la playa', 'Coworking incluido', 'Nómadas digitales', 'Comunidad activa'],
    keywords: 'palma coliving valencia, coliving patacona, villa coliving valencia'
  },
  'mikasita': {
    fullName: 'Mi Kasita',
    description: 'Mi Kasita es la red de coliving para estudiantes y Erasmus más grande de España, con residencias en Bilbao (Abando e Indautxu/Deusto). Habitaciones individuales y dobles desde 325€/mes con WiFi, suministros y eventos incluidos.',
    cities: ['Bilbao'],
    priceRange: '325€ - 450€',
    avgPrice: 380,
    highlights: ['Erasmus y estudiantes', 'Abando e Indautxu', 'Cerca de UPV/EHU y Deusto', 'Todo incluido'],
    keywords: 'mi kasita bilbao, mikasita coliving, coliving estudiantes bilbao, erasmus bilbao'
  },
  'astoncoliving': {
    fullName: 'Aston Coliving',
    description: 'Aston Coliving (Aston Wolf) ofrece coliving premium en Bilbao centro, Getxo y Portugalete. Dirigido a profesionales y nómadas digitales con estancia mínima de 1 mes, WiFi, limpieza, suministros y soporte 24/7.',
    cities: ['Bilbao', 'Getxo', 'Portugalete'],
    priceRange: '550€ - 750€',
    avgPrice: 650,
    highlights: ['Coliving premium', 'Centro y costa vizcaína', 'Sin aval', 'Coworking y zonas comunes'],
    keywords: 'aston coliving bilbao, aston wolf coliving, coliving getxo, coliving profesionales bilbao'
  },
  'bilbaohostel': {
    fullName: 'Bilbao Hostel Coliving',
    description: 'Bilbao Hostel ofrece coliving económico en Basurto-Zorroza, ideal para estancias cortas y largas. Habitaciones privadas en ambiente internacional con WiFi y acceso a zonas comunes del hostel.',
    cities: ['Bilbao'],
    priceRange: '350€ - 500€',
    avgPrice: 420,
    highlights: ['Precio económico', 'Basurto-Zorroza', 'Ambiente internacional', 'Estancias flexibles'],
    keywords: 'bilbao hostel coliving, coliving barato bilbao, hostel coliving bilbao'
  },
  'colivio': {
    fullName: 'Colivio',
    description: 'Colivio ofrece coliving flexible en el centro de Alicante para nómadas digitales y profesionales. Habitaciones privadas con coworking, gastos incluidos y contratos de 1 a 12 meses.',
    cities: ['Alicante'],
    priceRange: '550€ - 850€',
    avgPrice: 650,
    highlights: ['Centro de Alicante', 'Coworking', 'Flexible', 'Todo incluido'],
    keywords: 'colivio alicante, coliving centro alicante, colivio coliving'
  },
  'amro': {
    fullName: 'AMRO Estudiantes',
    description: 'AMRO Alicante es una residencia universitaria de nueva generación en San Vicente del Raspeig con concepto coliving. 290 camas, piscina, gimnasio y a 5 min de la Universidad de Alicante.',
    cities: ['Alicante'],
    priceRange: '650€ - 950€',
    avgPrice: 750,
    highlights: ['Residencia premium', 'Cerca de la UA', 'Piscina y gimnasio', 'Estudiantes'],
    keywords: 'amro alicante, amro estudiantes, residencia universitaria alicante'
  },
  'xior': {
    fullName: 'Xior Student Housing',
    description: 'Xior gestiona residencias de estudiantes todo incluido en Madrid, Barcelona, Sevilla, Málaga, Granada y Zaragoza. Habitaciones desde 420€/mes con piscina, gimnasio y limpieza quincenal.',
    cities: ['Madrid', 'Barcelona', 'Sevilla', 'Málaga', 'Granada', 'Zaragoza'],
    priceRange: '420€ - 959€',
    avgPrice: 650,
    highlights: ['6 ciudades en España', 'Todo incluido', 'Piscina y gimnasio', 'Estudiantes'],
    keywords: 'xior student housing, xior madrid, xior barcelona, xior zaragoza, xior granada, residencia estudiantes'
  },
  'kadora': {
    fullName: 'Kadora',
    description: 'Kadora Sevilla ofrece estudios y habitaciones en clúster en Nervión con piscina en azotea, gimnasio, cine y salas de estudio. Residencia premium para estudiantes universitarios.',
    cities: ['Sevilla'],
    priceRange: '820€ - 1035€',
    avgPrice: 900,
    highlights: ['Residencia premium', 'Piscina en azotea', 'Nervión', 'Estudiantes'],
    keywords: 'kadora sevilla, kadora residencia estudiantes, residencia nervion sevilla'
  },
  'seviatriana': {
    fullName: 'REST Residencia Estudiantes Triana',
    description: 'REST ofrece estudios completos en coliving en Triana, Sevilla. Estancias de 2 a 12 meses sin comisiones, con cocina privada y zonas comunes. Cerca del Campus de la Cartuja.',
    cities: ['Sevilla'],
    priceRange: '790€ - 900€',
    avgPrice: 830,
    highlights: ['Triana', 'Sin comisiones', 'Estudios completos', 'Cartuja'],
    keywords: 'rest triana sevilla, residencia estudiantes triana, coliving triana sevilla'
  },

  // ──────────────────────────────────────────────────────────────
  // UK — LONDON
  // ──────────────────────────────────────────────────────────────
  'arkliving': {
    fullName: 'Ark Living',
    description: 'Ark Living is one of London\'s top boutique coliving operators with properties in Shoreditch, Wembley Park and the Isle of Dogs. Private en-suite rooms in beautifully designed shared houses, all-inclusive from £1,200/mo. No agency fees, flexible contracts from 1 month, community events and fast WiFi included.',
    cities: ['London', 'Shoreditch', 'Wembley'],
    priceRange: '£1,200 - £1,800/mo',
    avgPrice: 1450,
    highlights: ['No agency fees', 'Shoreditch, Wembley Park & Isle of Dogs', 'Boutique coliving design', 'Community events included'],
    keywords: 'ark living london, ark living coliving, ark living shoreditch, ark living wembley, coliving london no agency fee'
  },
  'gravityco': {
    fullName: 'Gravity Co',
    description: 'Gravity Co is a flexible coliving operator with stylish properties across London — West Court Hounslow, Camden Town, Camden Lock, Finsbury Park, West Hampstead, Notting Hill and Marylebone. Private furnished rooms with all bills and WiFi included. Flexible stays from 1 month, no agency fees. Ideal for professionals and remote workers relocating to London.',
    cities: ['London', 'Hounslow', 'Camden', 'Finsbury Park', 'West Hampstead', 'Notting Hill', 'Marylebone'],
    priceRange: '£1,280 - £2,100/mo',
    avgPrice: 1550,
    highlights: ['Camden, Finsbury Park & West Hampstead', 'West Court Hounslow with gym & parking', 'Flexible stays from 1 month', 'All-inclusive pricing'],
    keywords: 'gravity co london, gravity coliving, gravity co camden, gravity co finsbury park, gravity co west hampstead, gravity co notting hill, coliving marylebone'
  },
  'folkcoliving': {
    fullName: 'Folk Coliving',
    description: 'Folk Coliving creates community-focused coliving homes in London. Their houses are beautifully furnished with designer interiors, communal garden spaces and curated events. Private rooms all-inclusive from £1,200/mo across multiple London neighbourhoods. No admin fees, flexible leases from 3 months.',
    cities: ['London'],
    priceRange: '£1,200 - £1,600/mo',
    avgPrice: 1380,
    highlights: ['Community-first coliving', 'Designer-furnished homes', 'Garden spaces', 'Curated community events'],
    keywords: 'folk coliving london, folk coliving rooms, coliving london community, boutique coliving london'
  },
  'cohabslondon': {
    fullName: 'Cohabs London',
    description: 'Cohabs London is the UK arm of the international Cohabs brand, with 8+ coliving houses clustered around Elephant & Castle, Clapham Common and Finsbury Park. Private furnished rooms from £1,391/mo, all-inclusive. Community events, cleaning and fast WiFi included. No admin fees, flexible leases.',
    cities: ['London', 'Elephant and Castle', 'Clapham', 'Finsbury Park'],
    priceRange: '£1,391 - £1,600/mo',
    avgPrice: 1480,
    highlights: ['8+ houses in London', 'Elephant & Castle cluster', 'All-inclusive pricing', 'International community'],
    keywords: 'cohabs london, cohabs elephant castle, cohabs clapham, coliving elephant castle, cohabs coliving london'
  },
  'nodelondon': {
    fullName: 'Node Living London',
    description: 'Node Living London offers premium furnished studios and co-living rooms in Brixton and Limehouse. Fully furnished private rooms with en-suite bathrooms, all-inclusive pricing (WiFi, utilities, cleaning). Flexible leases, no agency fees, rooftop terraces and coworking spaces.',
    cities: ['London', 'Brixton', 'Limehouse'],
    priceRange: '£1,742 - £2,562/mo',
    avgPrice: 2100,
    highlights: ['Brixton & Limehouse locations', 'En-suite private rooms', 'Rooftop terraces', 'Coworking included'],
    keywords: 'node living london, node coliving brixton, node living limehouse, premium coliving london'
  },
  'quintainliving': {
    fullName: 'Quintain Living',
    description: 'Quintain Living is Wembley Park\'s largest build-to-rent operator with 3,000+ homes. Studios and apartments in the heart of the new Wembley neighbourhood — steps from Wembley Stadium, SSE Arena and London Designer Outlet. Fast Metropolitan & Jubilee lines to central London in 12 minutes.',
    cities: ['London', 'Wembley'],
    priceRange: '£1,200 - £2,000/mo',
    avgPrice: 1600,
    highlights: ['3,000+ apartments in Wembley Park', '12 min to central London', 'Steps from Wembley Stadium', 'Premium amenities'],
    keywords: 'quintain living wembley, quintain living london, wembley park coliving, build to rent wembley, coliving wembley stadium'
  },
  'scape': {
    fullName: 'Scape Student Accommodation',
    description: 'Scape is a premium London student accommodation brand with 9 locations: Wembley, Mile End, Canada Water, Shoreditch, Bloomsbury, Kings Cross, Hammersmith and more. En-suite rooms and studios all-inclusive from £1,345/mo. Study spaces, gym, cinema room and concierge in every building.',
    cities: ['London', 'Wembley', 'Bloomsbury', 'Shoreditch', 'Kings Cross'],
    priceRange: '£1,345 - £2,881/mo',
    avgPrice: 1800,
    highlights: ['9 locations across London', 'Gym and cinema in every building', 'Study spaces 24/7', 'All utilities included'],
    keywords: 'scape london, scape student accommodation, scape wembley, scape bloomsbury, scape shoreditch, student accommodation london',
    forStudents: true
  },
  'canvas': {
    fullName: 'Canvas Accommodation',
    description: 'Canvas offers affordable premium student accommodation in Wembley Park. En-suite rooms and studio flats from £1,105/mo, fully furnished and all-inclusive. Walking distance from Wembley Park tube (Met & Jubilee lines) and close to University of Westminster, Brunel and Middlesex University.',
    cities: ['London', 'Wembley'],
    priceRange: '£1,105 - £1,400/mo',
    avgPrice: 1250,
    highlights: ['Affordable Wembley Park location', '4 min walk to tube', 'All-inclusive pricing', 'Study and social spaces'],
    keywords: 'canvas accommodation wembley, canvas coliving london, student accommodation wembley, canvas wembley park',
    forStudents: true
  },
  'lyvly': {
    fullName: 'Lyvly',
    description: 'Lyvly manages a network of coliving houses across South and West London — Clapham, Clapham Junction, Hackney, Stockwell, East Putney, Fulham and more. Private furnished rooms in refurbished Victorian houses, all-inclusive from £1,095/mo. Flexible leases, no agency fees, smart-lock entry.',
    cities: ['London', 'Clapham', 'Hackney', 'Fulham'],
    priceRange: '£1,095 - £1,580/mo',
    avgPrice: 1350,
    highlights: ['South & West London locations', 'Victorian refurbished houses', 'Smart-lock entry', 'No agency fees'],
    keywords: 'lyvly london, lyvly coliving, lyvly clapham, lyvly hackney, coliving south london, coliving west london'
  },
  'fizzyliving': {
    fullName: 'Fizzy Living',
    description: 'Fizzy Living is one of London\'s biggest purpose-built rental operators with 10+ developments across East and West London: Royal Docks, Poplar, Canning Town, Stepney Green, Lewisham, Hayes, Epsom and Walthamstow. Fully furnished studios and apartments, all-inclusive from £1,100/mo. Pet-friendly, no agency fees.',
    cities: ['London', 'Royal Docks', 'Walthamstow', 'Hayes'],
    priceRange: '£1,100 - £1,900/mo',
    avgPrice: 1400,
    highlights: ['10+ developments across London', 'Pet-friendly', 'No agency fees', 'Modern purpose-built apartments'],
    keywords: 'fizzy living london, fizzy living royal docks, fizzy living canning town, fizzy living walthamstow, build to rent london'
  },
  'masonfifth': {
    fullName: 'Mason & Fifth',
    description: 'Mason & Fifth is London\'s most design-led flex living destination at Westbourne Park, West London. Former London Taxi Drivers\' HQ reimagined as 332 private studios with a world-class wellness space (pool, sauna, steam room, gym), canal-side restaurant and recording studio. Studios from £1,995/mo.',
    cities: ['London', 'Westbourne Park'],
    priceRange: '£1,995 - £2,995/mo',
    avgPrice: 2400,
    highlights: ['332 studios at Grand Union Canal', 'Pool, sauna & steam room', 'Canal-side restaurant', 'Recording studio'],
    keywords: 'mason fifth westbourne park, mason and fifth london, flex living westbourne park, canal coliving london'
  },
  'vonder': {
    fullName: 'Vonder',
    description: 'Vonder is a premium co-living brand combining private furnished apartments with coworking spaces and an active community. Their Shoreditch, London location puts you in the heart of Silicon Roundabout — steps from Old Street station. Flexible contracts, all-inclusive pricing (WiFi, bills, cleaning), and access to Vonder\'s global network of coliving communities.',
    cities: ['London', 'Shoreditch'],
    priceRange: '£1,880/mo',
    avgPrice: 1880,
    highlights: ['Shoreditch / Old Street location', 'Built-in coworking spaces', 'Global community network', 'All-inclusive no agency fees'],
    keywords: 'vonder london, vonder shoreditch, vonder coliving, vonder coworking, coliving old street london'
  },
  'chapter': {
    fullName: 'Chapter Living',
    description: 'Chapter Living is London\'s largest student accommodation network with 15 buildings across the city — Shoreditch, Islington, Kings Cross, Lewisham, Bloomsbury, White City, London Bridge, South Bank, Westminster and more. En-suite rooms and studios all-inclusive from £1,036/mo. Every member gets Chapter Club access: entry to all 15 buildings, events, gym and study spaces.',
    cities: ['London', 'Shoreditch', 'Islington', 'Kings Cross'],
    priceRange: '£1,036 - £2,184/mo',
    avgPrice: 1500,
    highlights: ['15 locations across London', 'Chapter Club: access all buildings', 'Gym, cinema & study spaces', 'Shoreditch, Islington, Kings Cross'],
    keywords: 'chapter living london, chapter student accommodation, chapter shoreditch, chapter islington, chapter kings cross, chapter coliving london',
    forStudents: true
  },
  'aparto': {
    fullName: 'aparto Student Living',
    description: 'aparto is an international student accommodation brand with a London location in Kingston. Private en-suite rooms in a purpose-built building with gym, study spaces, social areas and on-site management. All-inclusive pricing, no agency fees, flexible leases aligned to academic year.',
    cities: ['London'],
    priceRange: '£1,000 - £1,400/mo',
    avgPrice: 1200,
    highlights: ['Kingston-upon-Thames location', 'On-site management team', 'Study spaces & gym', 'Academic year leases'],
    keywords: 'aparto london, aparto kingston, aparto student accommodation, coliving kingston london',
    forStudents: true
  },
  'yourtribe': {
    fullName: 'YourTRIBE',
    description: 'YourTRIBE is award-winning student accommodation across London — Elephant & Castle (Zone 1), Deptford, Southwark and South Bermondsey. All-inclusive rent covers WiFi, bills, insurance, free coffee daily, breakfast twice weekly, gym, rooftop terrace and resident events. Ranked #1 Student Property in South East London by StudentCrowd.',
    cities: ['London', 'Elephant and Castle', 'Deptford', 'Southwark', 'South Bermondsey'],
    priceRange: '£860 - £2,600/mo',
    avgPrice: 1450,
    highlights: ['4 London locations incl. Zone 1', 'Free coffee & breakfast included', 'Gym, rooftop & cinema rooms', 'Award-winning community events'],
    keywords: 'yourtribe london, yourtribe student accommodation, yourtribe elephant and castle, yourtribe deptford, coliving london students',
    forStudents: true
  },
  'uncle': {
    fullName: 'UNCLE',
    description: 'UNCLE is a leading Build-to-Rent operator across London with designer furnished apartments in Stockwell, Colindale, Wembley, Southall, Acton, Deptford, New Cross and Elephant & Castle. On-site managers, gyms, coworking spaces, 48-hour repairs and no agency fees. Pet-friendly at most locations.',
    cities: ['London', 'Stockwell', 'Wembley', 'Elephant and Castle', 'New Cross', 'Deptford'],
    priceRange: '£1,550 - £3,715/mo',
    avgPrice: 2200,
    highlights: ['8 London BTR locations', 'BoConcept furniture & premium appliances', 'Gym, sky lounges & coworking', 'No agency fees, pet-friendly'],
    keywords: 'uncle london, uncle coliving, uncle stockwell, uncle elephant and castle, build to rent london, furnished apartments london'
  },
  'dandi': {
    fullName: 'Dandi Living',
    description: 'Dandi Living reinvents city living with patented floating beds, modular furniture and smart studio apartments across Wembley, Harrow, Battersea and Brondesbury. All-inclusive furnished homes with fortnightly cleaning, gym, coworking, cinema rooms and vibrant resident communities.',
    cities: ['London', 'Wembley', 'Harrow', 'Battersea', 'Brondesbury'],
    priceRange: '£1,300 - £2,400/mo',
    avgPrice: 1650,
    highlights: ['5 London co-living sites', 'Patented floating bed & modular design', 'Gym, cinema & coworking included', 'From £1,300/mo studios'],
    keywords: 'dandi living london, dandi coliving, dandi wembley, dandi battersea, dandi brondesbury, build to rent london studios'
  },
  'iq': {
    fullName: 'iQ Student Accommodation',
    description: 'iQ is one of the UK\'s largest PBSA operators with 12+ London residences including Bloomsbury, Shoreditch, City, Aldgate, Hoxton, Bankside, Highbury, Vega and Greenwich. En-suite rooms, studios and shared apartments with all bills, WiFi, gym, cinema and 24/7 security included.',
    cities: ['London', 'Bloomsbury', 'Shoreditch', 'Aldgate', 'Hoxton', 'Bankside', 'Greenwich', 'Vauxhall'],
    priceRange: '£1,400 - £2,700/mo',
    avgPrice: 1750,
    highlights: ['12+ London student residences', 'All bills & WiFi included', 'Gym, cinema & sky lounges', 'From £340/week en-suites'],
    keywords: 'iq student accommodation london, iq bloomsbury, iq shoreditch, iq city london, student halls london, pbSA london',
    forStudents: true
  },
  'unite': {
    fullName: 'Unite Students',
    description: 'Unite Students is the UK\'s largest student accommodation provider with 30+ London properties including Stratford One, Stapleton House, East Central House, Moonraker Point and Pacific Court. All-inclusive weekly rent with WiFi, bills, contents insurance and 24/7 on-site teams.',
    cities: ['London', 'Stratford', 'Holloway', 'Bethnal Green', 'Tottenham', 'South Bank', 'Shoreditch', 'Hoxton'],
    priceRange: '£1,140 - £2,100/mo',
    avgPrice: 1300,
    highlights: ['30+ London student properties', 'All bills included in weekly rent', 'From £264/week en-suites', 'Olympic Park & Zone 1 locations'],
    keywords: 'unite students london, unite stratford one, unite stapleton house, student accommodation london, pbSA london unite',
    forStudents: true
  },
  'bloom': {
    fullName: 'Bloom Coliving',
    description: 'Bloom Coliving offers all-inclusive private rooms in professionally managed shared flats across East and Central London — Shoreditch, Hackney, Whitechapel, Bethnal Green and Wapping. Weekly cleaning, fibre WiFi, bills and council tax included. Flexible stays from 1 month for working professionals.',
    cities: ['London', 'Shoreditch', 'Hackney', 'Whitechapel', 'Bethnal Green', 'Wapping', 'Bow'],
    priceRange: '£980 - £1,490/mo',
    avgPrice: 1250,
    highlights: ['All-inclusive professional coliving', 'Weekly cleaner & eco products', 'From 1 month flexible stays', 'East & Central London locations'],
    keywords: 'bloom coliving london, bloom coliving shoreditch, professional coliving london, all inclusive room london, bloom coliving hackney',
    forStudents: false
  },
  'bamboo': {
    fullName: 'Bamboo Living',
    description: 'Bamboo Living is a boutique London lettings agency managing furnished apartments in premium buildings — Pan Peninsula, Arena Tower, Kings Cross, Canary Wharf, Battersea and Shoreditch. Smart-living homes for young professionals and international tenants with dedicated support teams in London and Beijing.',
    cities: ['London', 'Canary Wharf', 'Shoreditch', 'Battersea', 'Camden', 'Earls Court', 'Islington', 'South Bank'],
    priceRange: '£2,400 - £5,500/mo',
    avgPrice: 3200,
    highlights: ['Premium furnished London apartments', 'Pan Peninsula & Arena Tower', 'International tenant support', '1–3 bed managed lettings'],
    keywords: 'bamboo living london, bamboo living apartments, furnished flat london, pan peninsula bamboo, arena tower rent london',
    forStudents: false
  },
  'stayclub': {
    fullName: 'The Stay Club',
    description: 'The Stay Club has redefined student living in London since 2012 — all-inclusive private studios across Camden, Kentish Town and North Acton. Every room includes private bathroom, kitchenette, WiFi, bills, bed linen and towels. On-site gym, café, gaming areas and 24/7 security at Camden and Kentish Town.',
    cities: ['London', 'Camden', 'Kentish Town', 'North Acton'],
    priceRange: '£1,580 - £2,950/mo',
    avgPrice: 1750,
    highlights: ['All-inclusive student studios since 2012', 'Camden Market location', 'Gym, café & gaming areas', 'From £372/week studios'],
    keywords: 'the stay club london, stay club camden, stay club kentish town, student accommodation camden, all inclusive studio london',
    forStudents: true
  },
  'coluxliving': {
    fullName: 'Co-Lux Living',
    description: 'Co-Lux Living offers all-inclusive furnished rooms in managed shared homes for young professionals across South, West and Central London. Semi-serviced coliving with utilities, council tax and WiFi in one bill — no agency fees, flexible tenancies from 3 months. Rooms from £600/month depending on size and location.',
    cities: ['London', 'Clapham', 'Battersea', 'Fulham', 'East Putney', 'Shoreditch', 'Islington'],
    priceRange: '£760 - £1,180/mo',
    avgPrice: 920,
    highlights: ['All-inclusive professional coliving', 'South & West London portfolio', 'Ensuite and standard rooms', '4.7★ Google rating'],
    keywords: 'co-lux living london, colux living, coliving clapham, coliving battersea, all inclusive room london professionals'
  },
  'thecollective': {
    fullName: 'The Collective',
    description: 'The Collective Old Oak is premium West London co-living at Nash House, Acton — private studios, Twodio apartments and shared ensuite rooms from £1,279/month all-inclusive. Gym, spa, cinema, coworking, 24/7 concierge and bi-weekly cleaning. Minimum stay 90 days.',
    cities: ['London', 'Acton'],
    priceRange: '£1,279 - £1,950/mo',
    avgPrice: 1560,
    highlights: ['Premium Old Oak co-living', 'Gym, spa & cinema on-site', 'All-inclusive membership', '90-day minimum stay'],
    keywords: 'the collective old oak, collective acton coliving, coliving acton london, old oak lane studios'
  },
  'studentroost': {
    fullName: 'Student Roost',
    description: 'Student Roost is one of the UK\'s largest PBSA operators with award-winning residences in 22 cities. In London, Mannequin House in Walthamstow offers ensuite and studio rooms from £275/week — 30 seconds from Blackhorse Road tube, all bills included and 24/7 onsite support.',
    cities: ['London', 'Walthamstow'],
    priceRange: '£1,192 - £1,897/mo',
    avgPrice: 1350,
    highlights: ['Mannequin House Walthamstow', 'All bills included', 'Victoria Line — 6 stops to Euston', 'Ensuite & studio rooms'],
    keywords: 'student roost london, mannequin house walthamstow, student accommodation walthamstow, student roost mannequin house',
    forStudents: true
  },
  'domicoliving': {
    fullName: 'Domi Coliving',
    description: 'Domi Coliving offers affordable all-inclusive ensuite rooms in Charlton, Greenwich — one monthly bill covering rent, utilities, council tax, WiFi and bi-weekly cleaning. One minute from Charlton station, 20 minutes to London Bridge and Canary Wharf. Rolling contracts from 6 months.',
    cities: ['London', 'Charlton', 'Greenwich'],
    priceRange: '£925 - £1,025/mo',
    avgPrice: 975,
    highlights: ['All-inclusive Greenwich coliving', 'Charlton station 1 min walk', 'Bi-weekly cleaning included', 'Couples welcome'],
    keywords: 'domi coliving london, domi coliving charlton, coliving greenwich, all inclusive room charlton london'
  },

  // ──────────────────────────────────────────────────────────────
  // US — NEW YORK CITY
  // ──────────────────────────────────────────────────────────────
  'junehomes': {
    fullName: 'June Homes',
    description: 'June Homes is NYC\'s leading coliving and furnished apartment platform with 400+ rooms across Manhattan, Brooklyn and Queens. No broker fee, flexible leases from 1 month, all-inclusive pricing (utilities, WiFi, weekly cleaning). Tech-first platform: apply online in 5 minutes, sign digitally, move in within days.',
    cities: ['New York', 'Manhattan', 'Brooklyn', 'Queens'],
    priceRange: '$1,200 - $3,500/mo',
    avgPrice: 2000,
    highlights: ['400+ rooms across NYC', 'No broker fee', 'Apply & sign entirely online', 'Move in within days'],
    keywords: 'june homes nyc, june homes new york, june homes coliving, coliving nyc no broker fee, furnished rooms nyc'
  },
  'furnishedquarters': {
    fullName: 'Furnished Quarters',
    description: 'Furnished Quarters is NYC\'s premium furnished apartment and coliving provider with high-end rooms in Midtown Manhattan, the Financial District and Brooklyn. Corporate relocation specialists, all-inclusive pricing, flexible stay from 30 days. Ideal for business travelers, executives and professionals on assignment.',
    cities: ['New York', 'Manhattan', 'Brooklyn'],
    priceRange: '$2,500 - $6,000/mo',
    avgPrice: 3500,
    highlights: ['Premium Midtown & Financial District', 'Corporate relocation specialists', 'Stays from 30 days', 'Concierge service'],
    keywords: 'furnished quarters nyc, furnished quarters manhattan, corporate housing nyc, furnished apartments new york, coliving manhattan premium'
  },
  'roomrs': {
    fullName: 'Roomrs',
    description: 'Roomrs is a tech-forward coliving platform with furnished shared apartments across NYC — Manhattan, Brooklyn, Queens and Jersey City. No broker fee, flexible leases from 1 month, all-inclusive. Fully furnished private rooms in carefully curated apartments. Fast online application, instant approvals.',
    cities: ['New York', 'Manhattan', 'Brooklyn', 'Queens', 'Jersey City'],
    priceRange: '$1,200 - $2,800/mo',
    avgPrice: 1800,
    highlights: ['Manhattan, Brooklyn, Queens & Jersey City', 'No broker fee', 'Instant online approval', 'Fully furnished rooms'],
    keywords: 'roomrs nyc, roomrs coliving, roomrs new york, coliving nyc, furnished rooms new york no broker fee'
  },
  'nodenyc': {
    fullName: 'Node Living NYC',
    description: 'Node Living NYC offers design-forward furnished apartments and coliving rooms in Brooklyn and Manhattan. Premium interiors, rooftop decks, coworking areas and gym access. All-inclusive pricing with no broker fee. Flexible leases from 1 month, ideal for young professionals and remote workers.',
    cities: ['New York', 'Manhattan', 'Brooklyn'],
    priceRange: '$1,800 - $3,500/mo',
    avgPrice: 2400,
    highlights: ['Design-forward Brooklyn & Manhattan', 'Rooftop decks', 'Coworking & gym included', 'No broker fee'],
    keywords: 'node living nyc, node coliving new york, node brooklyn, node manhattan, premium coliving nyc'
  },
  'silverman': {
    fullName: 'Silverman',
    description: 'Silverman offers boutique furnished coliving rooms in the heart of Manhattan — Midtown, Upper West Side and Chelsea. No broker fee, all-inclusive pricing (utilities, WiFi, weekly cleaning), flexible leases. Premium quality at competitive rates for young professionals and relocators.',
    cities: ['New York', 'Manhattan'],
    priceRange: '$1,500 - $3,000/mo',
    avgPrice: 2000,
    highlights: ['Midtown & Chelsea Manhattan', 'No broker fee', 'Premium furnished rooms', 'Flexible leases'],
    keywords: 'silverman nyc, silverman coliving manhattan, furnished rooms midtown nyc, coliving chelsea manhattan'
  },
  'reserve': {
    fullName: 'Reserve Coliving',
    description: 'Reserve Coliving offers premium furnished rooms in Manhattan and Brooklyn for young professionals and corporate relocators. All-inclusive pricing, no broker fee, flexible leases from 1 month. Premium amenities including rooftop access, gym and communal workspaces.',
    cities: ['New York', 'Manhattan', 'Brooklyn'],
    priceRange: '$1,800 - $3,500/mo',
    avgPrice: 2400,
    highlights: ['Premium Manhattan & Brooklyn', 'No broker fee', 'Rooftop access', 'Coworking included'],
    keywords: 'reserve coliving nyc, reserve coliving manhattan, premium coliving new york, furnished rooms manhattan'
  },
  'squarelivinghub': {
    fullName: 'Square Living Hub',
    description: 'Square Living Hub provides affordable furnished coliving rooms in Queens and Brooklyn, NYC. Private rooms in shared apartments, no broker fee, utilities and WiFi included. Flexible month-to-month leases. Great value option for those new to NYC or budget-conscious professionals.',
    cities: ['New York', 'Queens', 'Brooklyn'],
    priceRange: '$1,200 - $2,000/mo',
    avgPrice: 1500,
    highlights: ['Queens & Brooklyn locations', 'Best value in NYC', 'No broker fee', 'Month-to-month leases'],
    keywords: 'square living hub nyc, coliving queens new york, affordable coliving nyc, furnished rooms queens brooklyn'
  },
  'blueground': {
    fullName: 'Blueground',
    description: 'Blueground is a global premium furnished apartment platform with listings in New York, London, Madrid and Barcelona. Hotel-quality design, flexible monthly stays, utilities included. Ideal for corporate relocations, digital nomads and mid-term stays (1-12 months).',
    cities: ['New York', 'Manhattan', 'Brooklyn', 'London', 'Madrid', 'Barcelona'],
    priceRange: '$2,900 - $8,000/mo · £1,200 - £6,000/mo · €1,200 - €4,500/mo',
    avgPrice: 4000,
    highlights: ['Hotel-quality furnished apartments', 'NYC, London, Madrid & Barcelona', 'No broker fee', 'Ideal for corporate relocation'],
    keywords: 'blueground nyc, blueground london, blueground madrid, blueground barcelona, furnished apartments, corporate housing, blueground coliving'
  },
  'common': {
    fullName: 'Common',
    description: 'Common is one of NYC\'s largest coliving operators with furnished private rooms across Manhattan, Brooklyn and Queens. Purpose-built and converted coliving buildings with coworking spaces, event programming and community managers. No broker fee, all-inclusive pricing (WiFi, utilities, weekly cleaning), flexible leases from 1 month.',
    cities: ['New York', 'Manhattan', 'Brooklyn', 'Queens'],
    priceRange: '$1,300 - $3,000/mo',
    avgPrice: 1900,
    highlights: ['Purpose-built coliving buildings', 'Coworking & event programming', 'Community managers on-site', 'No broker fee'],
    keywords: 'common coliving nyc, common new york, common coliving manhattan, common brooklyn, coliving buildings nyc'
  },
  'bungalow': {
    fullName: 'Bungalow',
    description: 'Bungalow offers vetted furnished coliving houses in NYC and across the US. Private rooms in shared homes with roommate matching, no broker fee, all utilities included. Tech-enabled management with the Bungalow app. Ideal for young professionals looking for genuine community living.',
    cities: ['New York', 'Brooklyn', 'Queens'],
    priceRange: '$1,200 - $2,500/mo',
    avgPrice: 1700,
    highlights: ['Vetted roommate matching', 'No broker fee', 'Bungalow app management', 'Community-focused living'],
    keywords: 'bungalow coliving nyc, bungalow new york, bungalow coliving brooklyn, roommate matching nyc, furnished rooms brooklyn'
  },
  'outsitenyc': {
    fullName: 'Outsite',
    description: 'Outsite offers premium coliving for digital nomads in Manhattan (Chelsea, Midtown) and London Canary Wharf (Curated ARK). Private furnished rooms, high-speed WiFi, coworking and community events.',
    cities: ['New York', 'Manhattan', 'London'],
    priceRange: '$2,400 - $2,600/mo · London from £2,200/mo',
    avgPrice: 2500,
    highlights: ['Digital nomad community', 'Coworking included', 'NYC + London', 'Flexible monthly stays'],
    keywords: 'outsite nyc, outsite london, outsite canary wharf, coliving coworking manhattan, digital nomad coliving'
  },
  'ollie': {
    fullName: 'Ollie',
    description: 'Ollie is a design-forward micro-living and coliving operator in New York City with locations in Long Island City (Queens) and Jersey City. Private furnished studios and coliving rooms in amenity-rich buildings: gym, rooftop, coworking and concierge. All-inclusive monthly pricing, no broker fee.',
    cities: ['New York', 'Queens', 'Jersey City'],
    priceRange: '$1,800 - $3,500/mo',
    avgPrice: 2500,
    highlights: ['Long Island City & Jersey City', 'Gym, rooftop & coworking', 'Design-forward interiors', 'No broker fee'],
    keywords: 'ollie nyc, ollie coliving new york, ollie long island city, ollie jersey city, micro living nyc'
  },
  'sharedeasy': {
    fullName: 'SharedEasy',
    description: 'SharedEasy is an affordable coliving platform in NYC with furnished private rooms in shared apartments across Manhattan, Brooklyn and Queens. No broker fee, all utilities and WiFi included, flexible month-to-month leases. Great for first-time NYC renters and those who want to avoid the traditional high-cost rental market.',
    cities: ['New York', 'Manhattan', 'Brooklyn', 'Queens'],
    priceRange: '$1,200 - $2,200/mo',
    avgPrice: 1600,
    highlights: ['Most affordable NYC coliving', 'No broker fee', 'Manhattan, Brooklyn & Queens', 'Month-to-month leases'],
    keywords: 'sharedeasy nyc, sharedeasy coliving, affordable coliving new york, furnished rooms nyc no broker, sharedeasy manhattan'
  }
};
